import {syntaxTree} from '@codemirror/language'
import {type Extension, RangeSetBuilder} from '@codemirror/state'
import {Decoration, type DecorationSet, EditorView, ViewPlugin, type ViewUpdate} from '@codemirror/view'

import styles from './active-block.module.css'

/**
 * Determines if a selection range overlaps with a node range
 */
function overlapsNode(selectionFrom: number, selectionTo: number, nodeFrom: number, nodeTo: number): boolean {
  return (
    (selectionFrom >= nodeFrom && selectionFrom < nodeTo) ||
    (selectionTo > nodeFrom && selectionTo <= nodeTo) ||
    (selectionFrom < nodeFrom && selectionTo > nodeTo)
  )
}

/**
 * Check if a node type represents a block-level element
 */
function isBlockNode(nodeName: string): boolean {
  return (
    // Headings
    nodeName.includes('Heading') ||
    nodeName.includes('ATXHeading') ||
    nodeName.includes('SetextHeading') ||
    // Lists
    nodeName === 'BulletList' ||
    nodeName === 'OrderedList' ||
    nodeName === 'ListItem' ||
    // Blockquotes
    nodeName === 'Blockquote' ||
    // Code blocks
    nodeName === 'CodeBlock' ||
    nodeName === 'FencedCode' ||
    // Tables
    nodeName === 'Table' ||
    nodeName === 'TableRow' ||
    // Paragraphs
    nodeName === 'Paragraph' ||
    // Horizontal rules
    nodeName === 'HorizontalRule' ||
    // Other block elements
    nodeName === 'HTMLBlock'
  )
}

/**
 * Find all block nodes that overlap with the current selection
 */
function findActiveBlocks(view: EditorView): Array<{from: number; to: number; name: string}> {
  const tree = syntaxTree(view.state)
  const selection = view.state.selection.main
  const activeBlocks: Array<{from: number; to: number; name: string}> = []

  // If there's no meaningful selection, return empty
  if (!selection || (selection.from === selection.to && selection.from === 0)) {
    return activeBlocks
  }

  // Traverse the syntax tree to find overlapping block nodes
  tree.iterate({
    from: selection.from,
    to: selection.to,
    enter: node => {
      if (isBlockNode(node.name) && overlapsNode(selection.from, selection.to, node.from, node.to)) {
        activeBlocks.push({
          from: node.from,
          to: node.to,
          name: node.name,
        })
      }
    },
  })

  return activeBlocks
}

/**
 * Builds active block decorations using syntax tree to identify blocks
 */
function buildActiveBlockDecorations(view: EditorView): DecorationSet {
  const builder = new RangeSetBuilder<Decoration>()
  const doc = view.state.doc
  const activeBlocks = findActiveBlocks(view)

  const linePositions = new Set<number>()
  for (const block of activeBlocks) {
    const fromLine = doc.lineAt(block.from)
    const toLine = doc.lineAt(block.to)

    for (let lineNum = fromLine.number; lineNum <= toLine.number; lineNum++) {
      const line = doc.line(lineNum)
      linePositions.add(line.from)
    }
  }

  // Sort array to fit CodeMirror's range set builder requirements
  const sortedPositions = Array.from(linePositions).sort((a, b) => a - b)

  for (const position of sortedPositions) {
    builder.add(position, position, Decoration.line({class: styles.activeBlock}))
  }

  return builder.finish()
}

/**
 * View plugin that provides active block decorations based on selection
 */
const activeBlockDecorationPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet

    constructor(view: EditorView) {
      this.decorations = buildActiveBlockDecorations(view)
    }

    update(update: ViewUpdate) {
      if (update.selectionSet || update.docChanged) {
        this.decorations = buildActiveBlockDecorations(update.view)
      }
    }
  },
  {
    decorations: v => v.decorations,
  },
)

/**
 * Extension that provides active block styling based on selection
 */
export const activeBlockExtension: Extension = [activeBlockDecorationPlugin]
